import React from 'react';
import { ShopConfigData } from '../types';
import {
  Form,
  Input,
  Radio,
  Select,
  FormInstance,
  InputNumber,
  Switch,
  Button,
  Row,
  Col,
} from 'antd';
import RegionSelect from '../components/settings/shopConfigFields/RegionSelect';
import GroupFields from '../components/settings/shopConfigFields/GroupFields';
import PhotoSelect from '../components/settings/shopConfigFields/PhotoSelect';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

const { Option } = Select;

export type FormFieldLike = {
  id?: number;
  code: string;
  type: string;
  description: string;
  storeRange?: any;
  storeDir?: string;
  value?: string;
  valueDetail?: { list?: string[]; [key: string]: any };
  remark?: any;
  isRequired?: boolean;
};

export type GetFormFieldOptions = {
  currentValues?: { [key: string]: any };
};

/**
 * Return properly formatted information
 *
 * @param field        Specific Field Information
 * @param formInfo     List of Fields
 * @param formInstance Form Instance
 * @param options      Optional extras: currentValues map for non-ShopConfigData callers
 * @returns            Formatted Form Field
 */
const getFormField: (
  field: FormFieldLike,
  formInfo: Array<ShopConfigData>,
  formInstance: FormInstance<any>,
  options?: GetFormFieldOptions
) => JSX.Element = (field, formInfo, formInstance, options) => {
  const key = field.code;
  const externalValue = options?.currentValues?.[field.code];
  const rawValue = externalValue !== undefined ? externalValue : field.value;
  const rules = field.isRequired ? [{ required: true }] : undefined;
  const commonProps = {
    key,
    label: field.description,
    name: field.code,
    rules,
    help: field.remark ? field.remark : undefined,
    style: { marginBottom: field.remark ? 16 : 24 },
  };

  switch (field.type) {
    default:
    case 'TEXT':
      return (
        <Form.Item {...commonProps} initialValue={rawValue}>
          <Input />
        </Form.Item>
      );
    case 'MANUAL':
      if (field.code === 'shop_country') {
        return (
          <RegionSelect
            key={key}
            field={field as ShopConfigData}
            country={field.value || ''}
            province={getFieldValue(formInfo, 'shop_province')}
            city={getFieldValue(formInfo, 'shop_city')}
          />
        );
      }
      break;
    case 'TEXTAREA':
      return (
        <Form.Item {...commonProps} initialValue={rawValue}>
          <Input.TextArea />
        </Form.Item>
      );
    case 'SELECT': {
      const storeRange = field.storeRange || {};
      return (
        <Form.Item {...commonProps} initialValue={rawValue}>
          <Radio.Group>
            {Object.keys(storeRange).map((option) => (
              <Radio key={option} value={option}>
                {storeRange[option]}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      );
    }
    case 'OPTIONS':
      return (
        <Form.Item {...commonProps} initialValue={rawValue}>
          <Select getPopupContainer={(triggerNode) => triggerNode.parentNode}>
            {Object.keys(field.storeRange || {}).map((size) => (
              <Option key={size} value={size}>
                {field.storeRange[size]}
              </Option>
            ))}
          </Select>
        </Form.Item>
      );
    case 'HIDDEN':
      return (
        <Form.Item {...commonProps} initialValue={rawValue}>
          <Input disabled />
        </Form.Item>
      );
    case 'GROUP':
      return (
        <GroupFields
          key={key}
          field={field as ShopConfigData}
          formInfo={formInfo}
          formInstance={formInstance}
        />
      );
    case 'PASSWORD':
      return (
        <Form.Item {...commonProps} initialValue={rawValue}>
          <Input.Password />
        </Form.Item>
      );
    case 'FILE':
      return (
        <PhotoSelect
          key={key}
          field={field as ShopConfigData}
          form={formInstance}
        />
      );
    case 'INTEGER':
    case 'LONG':
    case 'DOUBLE':
    case 'BIG_DECIMAL':
      return (
        <Form.Item {...commonProps} initialValue={rawValue}>
          <InputNumber />
        </Form.Item>
      );
    case 'DECIMAL':
      return (
        <Form.Item {...commonProps} initialValue={rawValue}>
          <InputNumber stringMode min="0" step="0.01" />
        </Form.Item>
      );
    case 'BOOLEAN':
      return (
        <Form.Item
          {...commonProps}
          initialValue={rawValue}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      );
    case 'LIST':
      return (
        <Form.Item
          key={key}
          label={field.description}
          rules={rules}
          help={field.remark ? field.remark : undefined}
          style={{ marginBottom: field.remark ? 16 : 24 }}
        >
          <Form.List
            name={field.code}
            initialValue={(field.valueDetail && field.valueDetail.list) || []}
          >
            {(items, { add, remove }) => (
              <Row gutter={[0, 8]}>
                <Col span={24}>
                  <Row gutter={[0, 8]}>
                    {items.map((item, index) => (
                      <Col span={24} key={index}>
                        <Row gutter={[8, 8]}>
                          <Col span={22}>
                            <Form.Item {...item} style={{ marginBottom: 0 }}>
                              <Input.TextArea
                                autoSize={{ minRows: 2, maxRows: 4 }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <MinusCircleOutlined
                              onClick={() => remove(item.name)}
                            />
                          </Col>
                        </Row>
                      </Col>
                    ))}
                  </Row>
                </Col>
                <Col span={22}>
                  <Button
                    type="dashed"
                    onClick={() => add('')}
                    block
                    icon={<PlusOutlined />}
                  />
                </Col>
              </Row>
            )}
          </Form.List>
        </Form.Item>
      );
  }
  return <div key={key}></div>;
};

export { getFormField };

/**
 * @param formInfo  Form Information
 * @param fieldName The Field we want the value of
 * @returns any     The value inside that field name
 */
const getFieldValue: (
  formInfo: Array<ShopConfigData>,
  fieldName: string
) => any = (formInfo, fieldName) => {
  const filteredArray = formInfo.filter((x) => x.code === fieldName);
  if (filteredArray.length === 1) {
    const field = filteredArray[0];
    return field.value;
  }
  return '';
};
